import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';
import { getDemoAuth } from '@/hooks/useAppAuth';
import AgreementSection from '@/components/feature/AgreementSection';

const PATIENT_CONDITIONS = ['거동가능', '부분도움필요', '와상', '중환자'];
const BODY_TYPES = ['비만', '보통', '마른'] as const;

export default function RegisterPatientPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUpdate, setIsUpdate] = useState(false);
  const [patientProfileId, setPatientProfileId] = useState<string | null>(null);

  // 환자 프로필 전용 필드
  const [diseaseName, setDiseaseName] = useState('');
  const [patientCondition, setPatientCondition] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [bodyType, setBodyType] = useState<string>('');
  const [visitType, setVisitType] = useState<'입주' | '출퇴근' | ''>('');
  const [toiletAssistance, setToiletAssistance] = useState<'필요' | '불필요' | ''>('');
  const [preferredCaregiverGender, setPreferredCaregiverGender] = useState<'남성' | '여성' | '무관' | ''>('무관');
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [thirdPartyChecked, setThirdPartyChecked] = useState(false);

  useEffect(() => {
    async function init() {
      const demo = getDemoAuth();
      const uid = demo?.userId || null;
      if (!uid) { setLoading(false); return; }
      setUserId(uid);

      const { data: existing } = await supabase
        .from('patient_profiles')
        .select('*')
        .eq('user_id', uid)
        .maybeSingle();

      if (existing) {
        setIsUpdate(true);
        setPatientProfileId(existing.id);
        setDiseaseName(existing.disease_name || '');
        setPatientCondition(existing.patient_condition || '');
        setHeight(existing.height || '');
        setWeight(existing.weight || '');
        setBodyType(existing.body_type || '');
        setVisitType(existing.visit_type || '');
        setToiletAssistance(existing.toilet_assistance || '');
        setPreferredCaregiverGender(existing.preferred_caregiver_gender || '무관');
      }

      // 환자 역할 부여
      const { data: profile } = await supabase
        .from('profiles')
        .select('roles')
        .eq('id', uid)
        .maybeSingle();

      if (profile && !profile.roles?.includes('patient')) {
        await supabase.from('profiles').update({
          roles: [...(profile.roles || []), 'patient'],
        }).eq('id', uid);

        const stored = localStorage.getItem('kc_demo_login');
        if (stored) {
          const parsed = JSON.parse(stored);
          const newRoles = [...(parsed.roles || []), 'patient'];
          parsed.roles = newRoles;
          localStorage.setItem('kc_demo_login', JSON.stringify(parsed));
        }
      }

      setLoading(false);
    }
    init();
  }, []);

  const isFormValid =
    toiletAssistance &&
    privacyChecked &&
    thirdPartyChecked;

  async function handleSubmit() {
    if (!userId) return;
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const payload = {
        user_id: userId,
        disease_name: diseaseName || null,
        patient_condition: patientCondition || null,
        height: height || null,
        weight: weight || null,
        body_type: bodyType || null,
        visit_type: visitType || null,
        toilet_assistance: toiletAssistance || null,
        preferred_caregiver_gender: preferredCaregiverGender || null,
      };

      if (isUpdate) {
        const { error: updateErr } = await supabase
          .from('patient_profiles')
          .update(payload)
          .eq('user_id', userId);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase
          .from('patient_profiles')
          .insert(payload);
        if (insertErr) throw insertErr;
      }

      setSuccess(isUpdate ? '환자 정보가 수정되었습니다.' : '환자 등록이 완료되었습니다.');
      setTimeout(() => navigate('/app', { replace: true }), 1200);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!userId) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <p className="text-gray-500 mb-4">로그인이 필요합니다.</p>
          <button onClick={() => navigate('/app/login')} className="text-[#2E7D6B] font-bold cursor-pointer">로그인하기</button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-white">

        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/app')} className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50">
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold text-gray-900">{isUpdate ? '환자 정보 수정' : '환자 등록'}</h1>
            <p className="text-xs text-gray-400">간병이 필요한 환자의 건강 정보를 입력해주세요</p>
          </div>
        </div>

        {/* 안내 */}
        <div className="p-4 rounded-xl bg-[#f0f9f6] border border-[#b8e6d8] mb-6">
          <div className="flex items-start gap-2.5">
            <i className="ri-information-line text-[#2E7D6B] mt-0.5" />
            <div className="text-xs text-gray-700 leading-relaxed">
              <p className="font-semibold mb-1">환자 프로필을 먼저 등록해주세요</p>
              <p>환자 기본정보는 한 번만 등록하면 됩니다. 간병 조건(장소, 기간, 비용)은 이후 <strong>간병매칭신청</strong>에서 따로 설정할 수 있어요.</p>
            </div>
          </div>
        </div>

        {/* 환자 건강 정보 */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-heart-pulse-line text-[#2E7D6B]" />
            <span className="text-sm font-bold text-[#2E7D6B]">환자 건강 정보</span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              병명 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <input
              type="text"
              value={diseaseName}
              onChange={(e) => setDiseaseName(e.target.value)}
              placeholder="예: 뇌졸중, 치매, 골절 등"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              환자 상태 <span className="text-gray-400 font-normal">(선택)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PATIENT_CONDITIONS.map((cond) => (
                <button
                  key={cond}
                  onClick={() => setPatientCondition(cond)}
                  className={`py-2.5 px-3 rounded-xl border-2 text-xs font-medium cursor-pointer transition-all ${
                    patientCondition === cond
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {cond}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              대소변 도움 필요 여부 <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-3">
              {(['필요', '불필요'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setToiletAssistance(opt)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${
                    toiletAssistance === opt
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">키 <span className="text-gray-400 font-normal">(선택)</span></label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="170"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">cm</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">체중 <span className="text-gray-400 font-normal">(선택)</span></label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="65"
                  className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
                />
                <span className="text-xs text-gray-400 whitespace-nowrap">kg</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">체질 <span className="text-gray-400 font-normal">(선택)</span></label>
            <div className="flex gap-2">
              {BODY_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setBodyType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${
                    bodyType === t
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">간병 방식 <span className="text-gray-400 font-normal">(선택)</span></label>
            <div className="flex gap-2">
              {(['입주', '출퇴근'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setVisitType(t)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${
                    visitType === t
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">간병인 선호 성별 <span className="text-gray-400 font-normal">(선택)</span></label>
            <div className="flex gap-2">
              {(['남성', '여성', '무관'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setPreferredCaregiverGender(opt)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${
                    preferredCaregiverGender === opt
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 에러/성공 메시지 */}
        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 text-xs text-red-500 text-center">{error}</div>}
        {success && <div className="mb-4 p-3 rounded-xl bg-green-50 text-xs text-green-600 text-center font-bold">{success}</div>}

        {/* 약관 동의 */}
        <div className="mb-6">
          <AgreementSection
            type="patient"
            privacyChecked={privacyChecked}
            thirdPartyChecked={thirdPartyChecked}
            onPrivacyChange={setPrivacyChecked}
            onThirdPartyChange={setThirdPartyChecked}
            disabled={saving}
          />
        </div>

        {/* 제출 버튼 */}
        <button
          onClick={handleSubmit}
          disabled={!isFormValid || saving}
          className="w-full py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50"
          style={{ backgroundColor: '#2E7D6B' }}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />저장 중...
            </span>
          ) : (
            isUpdate ? '환자 정보 수정하기' : '환자 등록하기'
          )}
        </button>

        <p className="text-[10px] text-gray-400 text-center mt-4 pb-4">
          등록 후 간병매칭신청에서 원하는 조건으로 간병을 신청할 수 있습니다
        </p>
      </div>
    </AppLayout>
  );
}